/**
 * Shared Quantity Attribute Configuration
 */

import * as gqlTypes from "@hydrologiq/gql/graphql"
import type { AttributeConfig } from "../../types"
import { validateQuantity as sharedValidateQuantity } from "../validators"

export { validateQuantity } from "../validators"

export const baseQuantityConfig: Partial<AttributeConfig> = {
  attribute: gqlTypes.ActivityAttributeKind.Quantity,
  validator: sharedValidateQuantity,
  order: 30,
}

/**
 * Quantity for Scheduled status
 * Context-aware label (Fill/Delivery/Collection quantity)
 */
export const quantityForScheduled = (
  activityKind: gqlTypes.ActivityKind,
  label?: string,
  inheritedStatuses: gqlTypes.ActivityEventKind[] = [gqlTypes.ActivityEventKind.Scheduled]
): AttributeConfig =>
  ({
    ...baseQuantityConfig,
    label: label ?? `${capitalize(activityKind)} Quantity (kg)`,
    visibility: "visible",
    editable: true,
    required: true,
    helpText: `Estimated quantity for ${activityKind}`,
    // Pre-populate from quantity_scheduled first (for suggested fulfilments), then order quantity
    defaultValue: (ctx) => {
      console.log("[quantityForScheduled] ctx", ctx)
      // Prefer quantity_scheduled if it's set (e.g., from suggested fulfilments)
      let quantity = ctx.activity.quantity_scheduled?.toString() ?? null
      if (quantity) return quantity
      // Fall back to order quantity if available and non-zero
      quantity = ctx.order?.quantity?.toString() ?? null
      if (quantity && parseFloat(quantity) > 0) return quantity
      return null
    },
  } as AttributeConfig)

export const quantityForStatus = (
  activityKind: gqlTypes.ActivityEventKind,
  label: string = "Quantity",
  helpText?: string
): AttributeConfig =>
  ({
    ...baseQuantityConfig,
    label: `${label} (kg)`,
    visibility: "visible",
    editable: true,
    required: true,
    helpText: helpText ?? `Quantity for ${activityKind}`,
    defaultValue: (ctx) =>
      ctx.activity.attributes?.find(
        (attr) => attr.attribute === gqlTypes.ActivityAttributeKind.Quantity && attr.event_kind === activityKind
      )?.value ?? null,
  } as AttributeConfig)

/**
 * Hidden quantity (for Started status)
 */
export const quantityHidden: AttributeConfig = {
  ...baseQuantityConfig,
  label: "Quantity (kg)",
  visibility: "hidden",
  editable: false,
  required: false,
} as AttributeConfig

// Helper to capitalize first letter
function capitalize(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1)
}
